import { InlineArticlePianoTemplate } from '@/app_components/PianoTemplateContainers/InlineArticlePianoTemplate';
import { isAdPosition } from '@/app_lib/htmlParser/inserter/rules/dotCom/embedAdSlots';
import { EmbedPriority, type ParserRule } from '@/app_lib/htmlParser/inserter/rules/types';
import { baseAdPlacementConfig } from '@/app_lib/utils/ads';
import { cn } from '@/app_lib/utils/utils';

interface EmbedPianoTemplateProps {
  noInstream: boolean;
}

/**
 * Determines whether a component should be inserted at the end of the article,
 * based on the total number of <p> elements encountered so far, the current index, 
 * the total number of elements, and the desired template placement.
 * 
 * @param totalP - The total number of <p> elements encountered so far.
 * @param index - The current index of nodes in the article.
 * @param totalElements - The total number of elements in the article.
 * @param templatePlacement - The slot at which the template is intended to be placed.

 * @returns True if the current index is the last element and the total number of ps encountered is smaller than the desired placement; otherwise, false.
 */
const shouldInsertAtEnd = (totalP: number, index: number, totalElements: number, templatePlacement: number) => {
  const isLastElement = index === totalElements;
  return isLastElement && totalP < templatePlacement;
};

/**
 * Calculates the slot placement index based on the given ad slot placement and frequency of ads.
 *
 * @param adSlotPlacement - The ordinal position of the ad slot (1-based index).
 * @param frequency - The frequency at which ads should be placed.
 * @returns The calculated ad placement index, or -1 if frequency is not provided.
 */
const getSlotPlacement = (adSlotPlacement: number, frequency: number): number => {
  if (!frequency) {
    return -1;
  }
  return (adSlotPlacement - 1) * frequency + baseAdPlacementConfig.firstAdPlacement;
};

export const embedPianoTemplate = ({ noInstream }: EmbedPianoTemplateProps): ParserRule => {
  // Variable for which ad slot the piano template goes after
  const adSlotPlacement: 1 | 2 | 3 | null = null; // TODO: figure out if we can optimizely this

  return {
    insertComponent: ({ nodeTracker, totalElements, index }) => {
      if (adSlotPlacement === null) {
        return <></>;
      }
      // Where we should be inserting the template for desktop and mobile based on the ad config and the desired placement
      const templatePlacementDesktop = getSlotPlacement(adSlotPlacement, baseAdPlacementConfig.desktopAdFrequency);
      const templatePlacementMobile = getSlotPlacement(adSlotPlacement, baseAdPlacementConfig.mobileAdFrequency);

      // Return nothing if we don't have a valid placement for either desktop or mobile
      if (templatePlacementDesktop === -1 || templatePlacementMobile === -1) {
        return <></>;
      }

      // To manage saving space on the server side render for CLS, we have to do some annoying logic here
      // We need to know if we're in a mobile or desktop slot, or if we should insert at the end for either mobile or desktop.
      // This ensures that when we're only reserving space for mobile or desktop slots, we don't end up with a giant empty space on the other view.

      // Check if we're in the target placement for either mobile or desktop
      const isTargetPlacementMobile = nodeTracker.p === templatePlacementMobile;
      const isTargetPlacementDesktop = nodeTracker.p === templatePlacementDesktop;

      // Check if we should insert at the end for either mobile or desktop
      const shouldInsertAtEndMobile = shouldInsertAtEnd(nodeTracker.p, index, totalElements, templatePlacementMobile);
      const shouldInsertAtEndDesktop = shouldInsertAtEnd(nodeTracker.p, index, totalElements, templatePlacementDesktop);

      // Check if we're in a mobile or desktop ad slot
      const isMobileSlot = isAdPosition(baseAdPlacementConfig.mobileAdFrequency, nodeTracker.p);
      const isDesktopSlot = isAdPosition(baseAdPlacementConfig.desktopAdFrequency, nodeTracker.p);

      if (isTargetPlacementMobile || isTargetPlacementDesktop || shouldInsertAtEndMobile || shouldInsertAtEndDesktop) {
        const needsMobileStyling =
          (isMobileSlot && !isDesktopSlot) || (shouldInsertAtEndMobile && !shouldInsertAtEndDesktop);
        const needsDesktopStyling =
          (isDesktopSlot && !isMobileSlot) || (shouldInsertAtEndDesktop && !shouldInsertAtEndMobile);
        return (
          <div
            // Set the minimum height for the ad slot based on the device type
            className={cn(
              needsMobileStyling && 'max-md:min-h-[404px] md:contents',
              needsDesktopStyling && 'md:min-h-[404px] max-md:contents',
              ((isMobileSlot && isDesktopSlot) || (shouldInsertAtEndMobile && shouldInsertAtEndDesktop)) &&
                'min-h-[404px]',
              'flex items-center',
            )}
          >
            <InlineArticlePianoTemplate
              isDesktopPlacement={isTargetPlacementDesktop || shouldInsertAtEndDesktop}
              isMobilePlacement={isTargetPlacementMobile || shouldInsertAtEndMobile}
              key='article-piano-template-plea-container'
            />
          </div>
        );
      }

      return <></>;
    },
    insertPosition: 'after',
    match: ({ nodeTracker, node, index, totalElements }) => {
      if (adSlotPlacement === null) {
        return false;
      }
      const isMobileSlot = isAdPosition(baseAdPlacementConfig.mobileAdFrequency, nodeTracker.p);
      const isDesktopSlot = isAdPosition(baseAdPlacementConfig.desktopAdFrequency, nodeTracker.p);
      const shouldInsertAtEndMobile = shouldInsertAtEnd(
        nodeTracker.p,
        index,
        totalElements,
        getSlotPlacement(adSlotPlacement, baseAdPlacementConfig.mobileAdFrequency),
      );
      const shouldInsertAtEndDesktop = shouldInsertAtEnd(
        nodeTracker.p,
        index,
        totalElements,
        getSlotPlacement(adSlotPlacement, baseAdPlacementConfig.desktopAdFrequency),
      );
      return (
        !noInstream &&
        node.type === 'p' &&
        (isMobileSlot || isDesktopSlot || shouldInsertAtEndMobile || shouldInsertAtEndDesktop)
      );
    },
    priority: EmbedPriority.Low,
  };
};
